import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { collection, getDocs, query, where, orderBy, doc, updateDoc, addDoc, deleteDoc } from 'firebase/firestore';
import { db } from '../../configs/firebase';
import { EAuthData } from '../Provider/EAuthProvider';
import { 
  FaPlus, FaEdit, FaTrash, FaEye, FaCopy, FaArchive, FaUndo, 
  FaChartLine, FaUsers, FaCalendar, FaClock, FaMapMarkerAlt,
  FaBuilding, FaFilter, FaSearch, FaSort, FaDownload, 
  FaCheckCircle, FaTimesCircle, FaExclamationTriangle,
  FaStar, FaArrowUp, FaArrowDown, FaSpinner, FaSortAmountUp, FaSortAmountDown,
  FaBell, FaUserCheck
} from 'react-icons/fa';
import { 
  APPLICATION_STATUSES,
  getApplicationWithTimeoutInfo
} from '../../components/timeoutSystem';

const ManagePositions = () => {
  const { user } = EAuthData();
  const navigate = useNavigate();
  const [positions, setPositions] = useState([]);
  const [applications, setApplications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);
  const [selectedPositions, setSelectedPositions] = useState([]);
  const [sortBy, setSortBy] = useState('createdAt');
  const [sortOrder, setSortOrder] = useState('desc');
  const [filters, setFilters] = useState({
    status: 'all',
    type: 'all',
    location: 'all',
    search: ''
  });

  const [positionAnalytics, setPositionAnalytics] = useState({});

  useEffect(() => {
    fetchData();
  }, [user]);

  const fetchData = async () => {
    if (!user?.userd?.uid) return;

    try {
      setLoading(true);
      
      // Fetch positions
      const positionsRef = collection(db, 'positions');
      const positionsQuery = query(
        positionsRef,
        where('employer_id', '==', user.userd.uid),
        orderBy('createdAt', 'desc')
      );
      const positionsSnapshot = await getDocs(positionsQuery);
      const positionsData = positionsSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      setPositions(positionsData);

      // Fetch applications for analytics
      const applicationsRef = collection(db, 'applications');
      const applicationsQuery = query(
        applicationsRef,
        where('employerId', '==', user.userd.uid),
        orderBy('appliedAt', 'desc')
      );
      const applicationsSnapshot = await getDocs(applicationsQuery);
      const applicationsData = applicationsSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      setApplications(applicationsData);

      // Calculate analytics
      calculatePositionAnalytics(positionsData, applicationsData);

    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  const calculatePositionAnalytics = (positionsData, applicationsData) => {
    // Add timeout info to applications
    const applicationsWithTimeout = applicationsData.map(app => 
      getApplicationWithTimeoutInfo(app)
    );

    const analytics = {};
    
    positionsData.forEach(position => {
      const positionApps = applicationsWithTimeout.filter(app => app.positionId === position.id);
      
      // Updated status counts for new system
      const pendingApps = positionApps.filter(app => app.status === APPLICATION_STATUSES.PENDING);
      const interviewApps = positionApps.filter(app => app.status === APPLICATION_STATUSES.INTERVIEW);
      const awaitingResponseApps = positionApps.filter(app => app.status === APPLICATION_STATUSES.ACCEPTED);
      const confirmedHires = positionApps.filter(app => app.status === APPLICATION_STATUSES.STUDENT_ACCEPTED);
      const studentDeclinedApps = positionApps.filter(app => app.status === APPLICATION_STATUSES.STUDENT_DECLINED);
      const rejectedApps = positionApps.filter(app => app.status === APPLICATION_STATUSES.REJECTED);
      const timeoutDeclinedApps = positionApps.filter(app => app.status === APPLICATION_STATUSES.TIMEOUT_DECLINED);
      const withdrawnApps = positionApps.filter(app => app.status === APPLICATION_STATUSES.WITHDRAWN);
      
      // Urgent offers (expiring soon)
      const urgentOffers = positionApps.filter(app => 
        app.status === APPLICATION_STATUSES.ACCEPTED && app.timeoutInfo?.isUrgent
      );
      
      // Updated conversion rate (confirmed hires / total applications)
      const conversionRate = positionApps.length > 0 ? (confirmedHires.length / positionApps.length * 100) : 0;
      
      // Offer acceptance rate (confirmed / (confirmed + declined + timeout))
      const totalOfferResponses = confirmedHires.length + studentDeclinedApps.length + timeoutDeclinedApps.length;
      const offerAcceptanceRate = totalOfferResponses > 0 ? (confirmedHires.length / totalOfferResponses * 100) : 0;
      
      const oneWeekAgo = new Date(Date.now() - 7 * 24 * 60 * 60 * 1000);
      const recentApps = positionApps.filter(app => new Date(app.appliedAt) >= oneWeekAgo);
      
      // Average time to hire (from application to confirmed hire)
      const avgTimeToHire = confirmedHires.length > 0 
        ? confirmedHires.reduce((acc, app) => {
            const applied = new Date(app.appliedAt);
            const confirmed = new Date(app.studentResponseAt || app.updatedAt);
            return acc + (confirmed - applied) / (1000 * 60 * 60 * 24);
          }, 0) / confirmedHires.length
        : 0;

      // Updated performance calculation based on confirmed hires
      let performance = 'poor';
      if (conversionRate > 15) performance = 'excellent';
      else if (conversionRate > 10) performance = 'good';
      else if (conversionRate > 5) performance = 'average';

      analytics[position.id] = {
        totalApplications: positionApps.length,
        pendingApplications: pendingApps.length,
        interviewApplications: interviewApps.length,
        awaitingResponseApplications: awaitingResponseApps.length,
        confirmedHires: confirmedHires.length,
        studentDeclinedApplications: studentDeclinedApps.length,
        rejectedApplications: rejectedApps.length,
        timeoutDeclinedApplications: timeoutDeclinedApps.length,
        withdrawnApplications: withdrawnApps.length,
        urgentOffers: urgentOffers.length,
        conversionRate,
        offerAcceptanceRate,
        recentApplications: recentApps.length,
        avgTimeToHire,
        performance
      };
    });
    
    setPositionAnalytics(analytics);
  };

  const duplicatePosition = async (position) => {
    setActionLoading(true);
    try {
      const duplicatedPosition = {
        ...position,
        name: `${position.name} (Copy)`,
        createdAt: new Date().toISOString(),
        isAdvertised: false,
        status: 'draft'
      };
      delete duplicatedPosition.id;
      
      await addDoc(collection(db, 'positions'), duplicatedPosition);
      await fetchData();
      alert('Position duplicated successfully!');
    } catch (error) {
      console.error('Error duplicating position:', error);
      alert('Error duplicating position. Please try again.');
    } finally {
      setActionLoading(false);
    }
  };

  const toggleArchivePosition = async (positionId, currentStatus) => {
    setActionLoading(true);
    try {
      const newStatus = currentStatus === 'archived' ? 'active' : 'archived';
      const positionRef = doc(db, 'positions', positionId);
      await updateDoc(positionRef, {
        status: newStatus,
        archivedAt: newStatus === 'archived' ? new Date().toISOString() : null,
        isAdvertised: newStatus === 'archived' ? false : true
      });
      
      await fetchData();
      alert(`Position ${newStatus === 'archived' ? 'archived' : 'restored'} successfully!`);
    } catch (error) {
      console.error('Error updating position status:', error);
      alert('Error updating position. Please try again.');
    } finally {
      setActionLoading(false);
    }
  };

  const deletePosition = async (positionId) => {
    if (!window.confirm('Are you sure you want to permanently delete this position? This action cannot be undone.')) {
      return;
    }
    
    setActionLoading(true);
    try {
      await deleteDoc(doc(db, 'positions', positionId));
      await fetchData();
      alert('Position deleted permanently!');
    } catch (error) {
      console.error('Error deleting position:', error);
      alert('Error deleting position. Please try again.');
    } finally {
      setActionLoading(false);
    }
  };

  const bulkArchive = async () => {
    if (selectedPositions.length === 0) return;
    
    setActionLoading(true);
    try {
      const batch = selectedPositions.map(id => 
        updateDoc(doc(db, 'positions', id), {
          status: 'archived',
          archivedAt: new Date().toISOString(),
          isAdvertised: false
        })
      );
      await Promise.all(batch);
      setSelectedPositions([]);
      await fetchData();
      alert(`${selectedPositions.length} positions archived successfully!`);
    } catch (error) {
      console.error('Error archiving positions:', error);
      alert('Error archiving positions. Please try again.');
    } finally {
      setActionLoading(false);
    }
  };

  const bulkDelete = async () => {
    if (selectedPositions.length === 0) return;
    
    if (!window.confirm(`Are you sure you want to permanently delete ${selectedPositions.length} positions? This action cannot be undone.`)) {
      return;
    }
    
    setActionLoading(true);
    try {
      const batch = selectedPositions.map(id => deleteDoc(doc(db, 'positions', id)));
      await Promise.all(batch);
      setSelectedPositions([]);
      await fetchData();
      alert(`${selectedPositions.length} positions deleted successfully!`);
    } catch (error) {
      console.error('Error deleting positions:', error);
      alert('Error deleting positions. Please try again.');
    } finally {
      setActionLoading(false);
    }
  };

  const exportPositions = () => {
    const csvData = filteredAndSortedPositions.map(position => {
      const analytics = positionAnalytics[position.id] || {};
      return {
        'Position Name': position.name,
        'Location': position.location,
        'Type': position.type,
        'Duration': position.duration,
        'Status': getStatusText(position),
        'Total Applications': analytics.totalApplications || 0,
        'Pending': analytics.pendingApplications || 0,
        'Awaiting Response': analytics.awaitingResponseApplications || 0,
        'Confirmed Hires': analytics.confirmedHires || 0,
        'Student Declined': analytics.studentDeclinedApplications || 0,
        'Rejected': analytics.rejectedApplications || 0,
        'Conversion Rate': `${analytics.conversionRate?.toFixed(1) || 0}%`,
        'Offer Acceptance Rate': `${analytics.offerAcceptanceRate?.toFixed(1) || 0}%`,
        'Created Date': new Date(position.createdAt.toDate()).toLocaleDateString()
      };
    });
    
    const csvContent = "data:text/csv;charset=utf-8," 
      + Object.keys(csvData[0]).join(",") + "\n"
      + csvData.map(row => Object.values(row).map(value => `"${value}"`).join(",")).join("\n");
    
    const encodedUri = encodeURI(csvContent);
    const link = document.createElement("a");
    link.setAttribute("href", encodedUri);
    link.setAttribute("download", `positions_${new Date().toISOString().split('T')[0]}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  // Filter and sort positions
  const filteredAndSortedPositions = positions
    .filter(position => {
      if (filters.status !== 'all') {
        if (filters.status === 'active' && (!position.isAdvertised || position.status === 'archived')) return false;
        if (filters.status === 'archived' && position.status !== 'archived') return false;
        if (filters.status === 'draft' && (position.isAdvertised || position.status === 'archived')) return false;
      }
      
      if (filters.type !== 'all' && position.type !== filters.type) return false;
      if (filters.location !== 'all' && position.location !== filters.location) return false;
      
      if (filters.search) {
        const searchTerm = filters.search.toLowerCase();
        return (
          position.name?.toLowerCase().includes(searchTerm) ||
          position.description?.toLowerCase().includes(searchTerm) ||
          position.location?.toLowerCase().includes(searchTerm)
        );
      }
      
      return true;
    })
    .sort((a, b) => {
      let aValue = a[sortBy];
      let bValue = b[sortBy];
      
      if (sortBy === 'createdAt' || sortBy === 'updatedAt') {
        aValue = new Date(aValue);
        bValue = new Date(bValue);
      }
      
      if (sortBy === 'applications') {
        aValue = positionAnalytics[a.id]?.totalApplications || 0;
        bValue = positionAnalytics[b.id]?.totalApplications || 0;
      }
      
      if (sortBy === 'conversionRate') {
        aValue = positionAnalytics[a.id]?.conversionRate || 0;
        bValue = positionAnalytics[b.id]?.conversionRate || 0;
      }
      
      if (sortOrder === 'asc') {
        return aValue > bValue ? 1 : -1;
      } else {
        return aValue < bValue ? 1 : -1;
      }
    });

  const uniqueTypes = [...new Set(positions.map(p => p.type).filter(Boolean))];
  const uniqueLocations = [...new Set(positions.map(p => p.location).filter(Boolean))];

  const getStatusColor = (position) => {
    if (position.status === 'archived') return 'bg-gray-100 text-gray-600 border-gray-300';
    if (!position.isAdvertised) return 'bg-yellow-100 text-yellow-700 border-yellow-300';
    return 'bg-green-100 text-green-700 border-green-300';
  };

  const getStatusText = (position) => {
    if (position.status === 'archived') return 'Archived';
    if (!position.isAdvertised) return 'Draft';
    return 'Active';
  };

  const getPerformanceColor = (performance) => {
    switch (performance) {
      case 'excellent': return 'text-green-600';
      case 'good': return 'text-blue-600';
      case 'average': return 'text-yellow-600';
      case 'poor': return 'text-red-600';
      default: return 'text-gray-600';
    }
  };

  const getPerformanceIcon = (performance) => {
    switch (performance) {
      case 'excellent': return <FaStar className="inline mr-1" />;
      case 'good': return <FaArrowUp className="inline mr-1" />;
      case 'average': return <FaClock className="inline mr-1" />;
      case 'poor': return <FaArrowDown className="inline mr-1" />;
      default: return <FaClock className="inline mr-1" />;
    }
  };

  const PositionCard = ({ position }) => {
    const analytics = positionAnalytics[position.id] || {};
    const isSelected = selectedPositions.includes(position.id);
    const hasUrgentOffers = analytics.urgentOffers > 0;
    
    return (
      <div className={`bg-white rounded-lg shadow-md border-2 transition-all duration-200 hover:shadow-lg overflow-hidden ${
        isSelected ? 'border-orange-300 bg-orange-50' : 
        hasUrgentOffers ? 'border-red-300 bg-red-50' : 'border-gray-200'
      }`}>
        {/* Header Section */}
        <div className="p-6 border-b border-gray-100">
          <div className="flex items-start justify-between">
            <div className="flex-1 min-w-0">
              <div className="flex items-center mb-2">
                <input
                  type="checkbox"
                  checked={isSelected}
                  onChange={(e) => {
                    if (e.target.checked) {
                      setSelectedPositions(prev => [...prev, position.id]);
                    } else {
                      setSelectedPositions(prev => prev.filter(id => id !== position.id));
                    }
                  }}
                  className="mr-3 rounded flex-shrink-0"
                />
                <h3 className="text-lg font-semibold text-gray-900 truncate" title={position.name}>
                  {position.name}
                </h3>
              </div>
              
              {/* Position Details */}
              <div className="space-y-2 mb-3">
                <div className="flex items-center text-sm text-gray-600">
                  <FaMapMarkerAlt className="mr-2 flex-shrink-0" />
                  <span className="truncate">{position.location || 'Location not specified'}</span>
                </div>
                <div className="flex items-center text-sm text-gray-600">
                  <FaCalendar className="mr-2 flex-shrink-0" />
                  <span className="truncate">{position.type || 'Type not specified'}</span>
                </div>
                <div className="flex items-center text-sm text-gray-600">
                  <FaClock className="mr-2 flex-shrink-0" />
                  <span className="truncate">{position.duration || 'Duration not specified'}</span>
                </div>
              </div>
              
              {/* Status Badge and Urgent Alert */}
              <div className="flex items-center space-x-2">
                <div className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-medium border ${getStatusColor(position)}`}>
                  {getStatusText(position)}
                </div>
                {hasUrgentOffers && (
                  <div className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-red-100 text-red-800 border border-red-200">
                    <FaExclamationTriangle className="mr-1" />
                    {analytics.urgentOffers} Urgent
                  </div>
                )}
              </div>
            </div>
            
            {/* Performance Indicator */}
            {analytics.performance && (
              <div className={`text-right flex-shrink-0 ml-4 ${getPerformanceColor(analytics.performance)}`}>
                <div className="text-xs font-medium whitespace-nowrap">
                  {getPerformanceIcon(analytics.performance)}
                  {analytics.performance.toUpperCase()}
                </div>
                <div className="text-xs text-gray-500 whitespace-nowrap">
                  {analytics.conversionRate?.toFixed(1) || 0}% conversion
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Enhanced Analytics Section */}
        <div className="p-6 border-b border-gray-100">
          <div className="grid grid-cols-2 gap-4 mb-4">
            <div className="text-center min-w-0">
              <div className="text-2xl font-bold text-blue-600">
                {analytics.totalApplications || 0}
              </div>
              <div className="text-xs text-gray-600">Total Applications</div>
              {analytics.recentApplications > 0 && (
                <div className="text-xs text-green-600 truncate">
                  +{analytics.recentApplications} this week
                </div>
              )}
            </div>
            <div className="text-center min-w-0">
              <div className="text-2xl font-bold text-green-600">
                {analytics.confirmedHires || 0}
              </div>
              <div className="text-xs text-gray-600">Confirmed Hires</div>
              {analytics.avgTimeToHire > 0 && (
                <div className="text-xs text-gray-500 truncate">
                  {analytics.avgTimeToHire.toFixed(0)} days avg
                </div>
              )}
            </div>
          </div>

          {/* Additional metrics row */}
          <div className="grid grid-cols-3 gap-2 mb-4 text-center">
            <div className="min-w-0">
              <div className="text-sm font-bold text-yellow-600">
                {analytics.pendingApplications || 0}
              </div>
              <div className="text-xs text-gray-600">Pending</div>
            </div>
            <div className="min-w-0">
              <div className="text-sm font-bold text-blue-600">
                {analytics.awaitingResponseApplications || 0}
              </div>
              <div className="text-xs text-gray-600">Awaiting</div>
            </div>
            <div className="min-w-0">
              <div className="text-sm font-bold text-purple-600">
                {analytics.interviewApplications || 0}
              </div>
              <div className="text-xs text-gray-600">Interview</div>
            </div>
          </div>
          
          {/* Enhanced Application Pipeline */}
          {analytics.totalApplications > 0 && (
            <div className="mt-4">
              <div className="flex items-center justify-between text-xs text-gray-600 mb-2">
                <span className="truncate">Application Pipeline</span>
                <span className="flex-shrink-0 ml-2">{analytics.totalApplications} total</span>
              </div>
              <div className="flex rounded-full overflow-hidden h-2 bg-gray-200 w-full">
                {analytics.pendingApplications > 0 && (
                  <div 
                    className="bg-yellow-400" 
                    style={{ width: `${(analytics.pendingApplications / analytics.totalApplications) * 100}%` }}
                    title={`${analytics.pendingApplications} Pending`}
                  />
                )}
                {analytics.interviewApplications > 0 && (
                  <div 
                    className="bg-purple-400" 
                    style={{ width: `${(analytics.interviewApplications / analytics.totalApplications) * 100}%` }}
                    title={`${analytics.interviewApplications} Interview`}
                  />
                )}
                {analytics.awaitingResponseApplications > 0 && (
                  <div 
                    className="bg-blue-400" 
                    style={{ width: `${(analytics.awaitingResponseApplications / analytics.totalApplications) * 100}%` }}
                    title={`${analytics.awaitingResponseApplications} Awaiting Response`}
                  />
                )}
                {analytics.confirmedHires > 0 && (
                  <div 
                    className="bg-green-400" 
                    style={{ width: `${(analytics.confirmedHires / analytics.totalApplications) * 100}%` }}
                    title={`${analytics.confirmedHires} Confirmed Hires`}
                  />
                )}
                {analytics.studentDeclinedApplications > 0 && (
                  <div 
                    className="bg-orange-400" 
                    style={{ width: `${(analytics.studentDeclinedApplications / analytics.totalApplications) * 100}%` }}
                    title={`${analytics.studentDeclinedApplications} Student Declined`}
                  />
                )}
                {analytics.rejectedApplications > 0 && (
                  <div 
                    className="bg-red-400" 
                    style={{ width: `${(analytics.rejectedApplications / analytics.totalApplications) * 100}%` }}
                    title={`${analytics.rejectedApplications} Rejected`}
                  />
                )}
              </div>
            </div>
          )}

          {/* Offer acceptance rate for positions with offers */}
          {analytics.awaitingResponseApplications > 0 || analytics.confirmedHires > 0 || analytics.studentDeclinedApplications > 0 ? (
            <div className="mt-3 text-center">
              <div className="text-xs text-gray-600">Offer Acceptance Rate</div>
              <div className="text-sm font-bold text-green-600">
                {analytics.offerAcceptanceRate?.toFixed(1) || 0}%
              </div>
            </div>
          ) : null}
        </div>

        {/* Action Buttons Section */}
        <div className="p-6">
          <div className="space-y-2">
            {/* Primary Actions Row */}
            <div className="flex gap-2">
              <Link
                to={`/employer/positions/edit/${position.id}`}
                className="flex items-center justify-center px-3 py-2 bg-blue-100 text-blue-700 rounded-md hover:bg-blue-200 text-sm font-medium transition-colors flex-1"
              >
                <FaEdit className="mr-2" />
                Edit
              </Link>
              
              <button
                onClick={() => duplicatePosition(position)}
                disabled={actionLoading}
                className="flex items-center justify-center px-3 py-2 bg-purple-100 text-purple-700 rounded-md hover:bg-purple-200 text-sm font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex-1"
              >
                <FaCopy className="mr-2" />
                Duplicate
              </button>
            </div>
            
            {/* Secondary Actions Row */}
            <div className="flex gap-2">
              {position.status !== 'archived' ? (
                <button
                  onClick={() => toggleArchivePosition(position.id, position.status)}
                  disabled={actionLoading}
                  className="flex items-center justify-center px-3 py-2 bg-gray-100 text-gray-700 rounded-md hover:bg-gray-200 text-sm font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex-1"
                >
                  <FaArchive className="mr-2" />
                  Archive
                </button>
              ) : (
                <button
                  onClick={() => toggleArchivePosition(position.id, position.status)}
                  disabled={actionLoading}
                  className="flex items-center justify-center px-3 py-2 bg-green-100 text-green-700 rounded-md hover:bg-green-200 text-sm font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex-1"
                >
                  <FaUndo className="mr-2" />
                  Restore
                </button>
              )}
              
              <button
                onClick={() => deletePosition(position.id)}
                disabled={actionLoading}
                className="flex items-center justify-center px-3 py-2 bg-red-100 text-red-700 rounded-md hover:bg-red-200 text-sm font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex-1"
              >
                <FaTrash className="mr-2" />
                Delete
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  };

  const AnalyticsOverview = () => {
    const totalPositions = positions.length;
    const activePositions = positions.filter(p => p.isAdvertised && p.status !== 'archived').length;
    const archivedPositions = positions.filter(p => p.status === 'archived').length;
    const draftPositions = positions.filter(p => !p.isAdvertised && p.status !== 'archived').length;
    
    const totalApplications = Object.values(positionAnalytics).reduce((sum, analytics) => sum + (analytics.totalApplications || 0), 0);
    const totalConfirmedHires = Object.values(positionAnalytics).reduce((sum, analytics) => sum + (analytics.confirmedHires || 0), 0);
    const totalAwaitingResponse = Object.values(positionAnalytics).reduce((sum, analytics) => sum + (analytics.awaitingResponseApplications || 0), 0);
    const totalUrgentOffers = Object.values(positionAnalytics).reduce((sum, analytics) => sum + (analytics.urgentOffers || 0), 0);
    const avgConversionRate = totalApplications > 0 ? (totalConfirmedHires / totalApplications * 100) : 0;
    
    return (
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-6 mb-8">
        <div className="bg-white p-6 rounded-lg shadow-md border border-gray-200">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-sm font-medium text-gray-600">Total Positions</h3>
              <p className="text-xs text-blue-600">{activePositions} active</p>
            </div>
            <FaBuilding className="h-8 w-8 text-blue-600" />
          </div>
        </div>

        <div className="bg-white p-6 rounded-lg shadow-md border border-gray-200">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-sm font-medium text-gray-600">Applications Received</h3>
              <p className="text-2xl text-green-600 font-bold">{totalApplications}</p>
              <p className="text-xs text-green-600">{totalConfirmedHires} confirmed hires</p>
            </div>
            <FaUsers className="h-8 w-8 text-green-600" />
          </div>
        </div>

        <div className="bg-white p-6 rounded-lg shadow-md border border-gray-200">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-sm font-medium text-gray-600">Awaiting Response</h3>
              <p className="text-2xl text-cyan-600 font-bold">{totalAwaitingResponse}</p>
              <p className="text-xs text-cyan-600">
                {totalUrgentOffers > 0 ? `${totalUrgentOffers} urgent` : 'Offers sent'}
              </p>
            </div>
            <FaBell className="h-8 w-8 text-cyan-600" />
          </div>
        </div>

        <div className="bg-white p-6 rounded-lg shadow-md border border-gray-200">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-sm font-medium text-gray-600">Conversion Rate</h3>
              <p className="text-2xl text-purple-600 font-bold">{avgConversionRate.toFixed(1)}%</p>
              <p className="text-xs text-purple-600">Average across positions</p>
            </div>
            <FaChartLine className="h-8 w-8 text-purple-600" />
          </div>
        </div>

        <div className="bg-white p-6 rounded-lg shadow-md border border-gray-200">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-sm font-medium text-gray-600">Drafts & Archived</h3>
              <p className="text-2xl text-orange-800 font-bold">{draftPositions + archivedPositions}</p>
              <p className="text-xs text-orange-800">{draftPositions} drafts, {archivedPositions} archived</p>
            </div>
            <FaArchive className="h-8 w-8 text-orange-800" />
          </div>
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-orange-500 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading positions...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 p-4 md:p-8">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:justify-between md:items-center mb-8">
          <div>
            <h1 className="text-2xl md:text-3xl font-bold text-gray-900 mb-2">Manage Positions</h1>
            <p className="text-gray-600">Create, edit, and manage your internship positions with comprehensive analytics</p>
          </div>
          <div className="flex flex-col sm:flex-row space-y-2 sm:space-y-0 sm:space-x-3 mt-4 md:mt-0">
            <button
              onClick={exportPositions}
              className="flex items-center justify-center bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-md font-medium transition-colors"
            >
              <FaDownload className="mr-2" />
              Export CSV
            </button>
            <Link
              to="/employer/positions/add"
              className="flex items-center justify-center bg-orange-500 hover:bg-orange-600 text-white px-4 py-2 rounded-md font-medium transition-colors"
            >
              <FaPlus className="mr-2" />
              Add New Position
            </Link>
          </div>
        </div>

        {/* Enhanced Analytics Overview */}
        <AnalyticsOverview />

        {/* Urgent Offers Alert */}
        {Object.values(positionAnalytics).some(analytics => analytics.urgentOffers > 0) && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
            <div className="flex items-center">
              <FaExclamationTriangle className="h-5 w-5 text-red-600 mr-3" />
              <p className="text-red-800">
                <strong>
                  {Object.values(positionAnalytics).reduce((sum, analytics) => sum + (analytics.urgentOffers || 0), 0)}
                </strong> offer{Object.values(positionAnalytics).reduce((sum, analytics) => sum + (analytics.urgentOffers || 0), 0) !== 1 ? 's' : ''} across your positions are expiring soon! 
                Check your positions with urgent offers below.
              </p>
            </div>
          </div>
        )}

        {/* Controls */}
        <div className="flex flex-col md:flex-row md:justify-between md:items-center mb-6 space-y-4 md:space-y-0">
          {/* Bulk Actions */}
          {selectedPositions.length > 0 && (
            <div className="flex items-center space-x-2">
              <span className="px-3 py-2 bg-blue-100 text-blue-800 rounded-md text-sm font-medium">
                {selectedPositions.length} selected
              </span>
              <button
                onClick={bulkArchive}
                disabled={actionLoading}
                className="flex items-center px-3 py-2 bg-gray-500 text-white rounded-md text-sm hover:bg-gray-600 disabled:opacity-50"
              >
                {actionLoading ? <FaSpinner className="animate-spin mr-1" /> : <FaArchive className="mr-1" />}
                Archive All
              </button>
              <button
                onClick={bulkDelete}
                disabled={actionLoading}
                className="flex items-center px-3 py-2 bg-red-500 text-white rounded-md text-sm hover:bg-red-600 disabled:opacity-50"
              >
                {actionLoading ? <FaSpinner className="animate-spin mr-1" /> : <FaTrash className="mr-1" />}
                Delete All
              </button>
              <button
                onClick={() => setSelectedPositions([])}
                className="px-3 py-2 bg-gray-300 text-gray-700 rounded-md text-sm hover:bg-gray-400"
              >
                Clear Selection
              </button>
            </div>
          )}
        </div>

        {/* Filters and Search */}
        <div className="bg-white p-4 md:p-6 rounded-xl shadow-md border border-gray-200 mb-8">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
            {/* Search */}
            <div className="relative">
              <FaSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
              <input
                type="text"
                value={filters.search}
                onChange={(e) => setFilters(prev => ({ ...prev, search: e.target.value }))}
                placeholder="Search positions..."
                className="pl-10 pr-4 py-2 w-full border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500"
              />
            </div>
            
            {/* Status Filter */}
            <select
              value={filters.status}
              onChange={(e) => setFilters(prev => ({ ...prev, status: e.target.value }))}
              className="px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500"
            >
              <option value="all">All Status</option>
              <option value="active">Active</option>
              <option value="draft">Draft</option>
              <option value="archived">Archived</option>
            </select>
            
            {/* Type Filter */}
            <select
              value={filters.type}
              onChange={(e) => setFilters(prev => ({ ...prev, type: e.target.value }))}
              className="px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500"
            >
              <option value="all">All Types</option>
              {uniqueTypes.map(type => (
                <option key={type} value={type}>{type}</option>
              ))}
            </select>
            
            {/* Location Filter */}
            <select
              value={filters.location}
              onChange={(e) => setFilters(prev => ({ ...prev, location: e.target.value }))}
              className="px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500"
            >
              <option value="all">All Locations</option>
              {uniqueLocations.map(location => (
                <option key={location} value={location}>{location}</option>
              ))}
            </select>
            
            {/* Sort */}
            <div className="flex space-x-2">
              <select
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value)}
                className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500"
              >
                <option value="createdAt">Created Date</option>
                <option value="name">Name</option>
                <option value="applications">Applications</option>
                <option value="conversionRate">Conversion Rate</option>
              </select>
              <button
                onClick={() => setSortOrder(prev => prev === 'asc' ? 'desc' : 'asc')}
                className="px-3 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
              >
                {sortOrder === 'asc' ? <FaSortAmountUp /> : <FaSortAmountDown />}
              </button>
            </div>
          </div>
        </div>

        {/* Positions Grid */}
        {filteredAndSortedPositions.length === 0 ? (
          <div className="text-center py-12">
            <FaBuilding className="mx-auto h-12 w-12 text-gray-400 mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">No positions found</h3>
            <p className="text-gray-600">
              {positions.length === 0 
                ? "You haven't created any positions yet."
                : "No positions match your current filters."
              }
            </p>
            {positions.length === 0 && (
              <Link
                to="/employer/positions/add"
                className="inline-flex items-center mt-4 bg-orange-500 hover:bg-orange-600 text-white px-6 py-3 rounded-lg font-medium transition-colors"
              >
                <FaPlus className="mr-2" />
                Create Your First Position
              </Link>
            )}
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredAndSortedPositions.map(position => (
              <PositionCard key={position.id} position={position} />
            ))}
          </div>
        )}

        {/* Loading Overlay */}
        {actionLoading && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg p-6 flex items-center space-x-3">
              <FaSpinner className="animate-spin h-6 w-6 text-orange-500" />
              <span className="text-gray-900">Processing...</span>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ManagePositions;